import { useCallback, useRef, useState } from "react";
import { defineMessages } from "react-intl";

import { useFormatMessage } from "../Intl/Context";
import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_SUCCESS } from "../Alerts/Alert";

import { submitComment } from "../../mutations/SubmitCommentMutation";
import { updateComment } from "../../mutations/UpdateCommentMutation";
import { deleteComment } from "../../mutations/DeleteCommentMutation";

// Manages the full inline comment lifecycle: form open/close state, create,
// edit, update and delete operations. Designed to be used by
// SuggestionCardContent so the trigger button and body can live in separate
// DOM locations.
//
const useInlineComment = ({ commentable }) => {
  const commentFormRef = useRef();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [publishedComment, setPublishedComment] = useState();

  const t = useFormatMessage();
  const { showAlert } = useAlert();

  const handleOpenForm = useCallback(() => {
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setIsEditing(false);
  }, []);

  const handleCreate = useCallback(
    content => {
      if (!commentable) return;

      setIsSubmitting(true);

      submitComment(commentable, content, response => {
        setIsSubmitting(false);

        if (response.submitComment.success) {
          const comment = response.submitComment.newCommentEdge?.node;

          setPublishedComment(comment);
          setIsFormOpen(false);

          showAlert({
            message: t(intlMessages.createSuccess),
            type: ALERT_TYPE_SUCCESS
          });
        }
      });
    },
    [commentable, showAlert, t]
  );

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setIsFormOpen(true);
  }, []);

  const handleUpdate = useCallback(
    content => {
      setIsSubmitting(true);

      updateComment(publishedComment.id, content, response => {
        setIsSubmitting(false);

        if (response.updateComment.success) {
          setPublishedComment(response.updateComment.comment);
          setIsEditing(false);
          setIsFormOpen(false);

          showAlert({
            message: t(intlMessages.updateSuccess),
            type: ALERT_TYPE_SUCCESS
          });
        }
      });
    },
    [publishedComment, showAlert, t]
  );

  const handleDelete = useCallback(() => {
    if (!publishedComment) return;

    setIsSubmitting(true);

    deleteComment(publishedComment.id, response => {
      setIsSubmitting(false);

      if (response.deleteComment.success) {
        setPublishedComment(null);
        setIsFormOpen(false);
        setIsEditing(false);

        showAlert({
          message: t(intlMessages.deleteSuccess),
          type: ALERT_TYPE_SUCCESS
        });
      }
    });
  }, [publishedComment, showAlert, t]);

  return {
    commentFormRef,
    isFormOpen,
    isEditing,
    isSubmitting,
    publishedComment,
    handleOpenForm,
    handleCloseForm,
    handleEdit,
    handleCreate,
    handleUpdate,
    handleDelete
  };
};

export default useInlineComment;

const intlMessages = defineMessages({
  createSuccess: {
    id: "inlineCommentFormInput.createSuccess",
    defaultMessage: "Votre commentaire a bien été ajouté"
  },
  updateSuccess: {
    id: "inlineCommentFormInput.updateSuccess",
    defaultMessage: "Votre commentaire a bien été modifié"
  },
  deleteSuccess: {
    id: "inlineCommentFormInput.deleteSuccess",
    defaultMessage: "Votre commentaire a bien été supprimé"
  }
});
